import type { Branding } from '../theme/theme'

export interface SessionUser {
  id: string
  email: string
  role: 'PERSONAL' | 'STUDENT'
  personal: {
    id?: string
    slug: string
    displayName: string
    businessName: string
  }
  student?: {
    id: string
    name: string
    photoUrl: string | null
    goal: string | null
    status: string
  }
  branding: Branding
}

export interface StoredSession {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

/**
 * Chave por slug: dois personais no mesmo navegador não compartilham sessão.
 * O aluno pode ter contas em /app/arthur-performance e /app/fit-mariana
 * simultaneamente. Ver docs/specs/02-authentication.md.
 */
export function sessionKey(slug?: string): string {
  return slug ? `steingym.session.${slug}` : 'steingym.session'
}

export function readSession(slug?: string): StoredSession | null {
  try {
    const raw = localStorage.getItem(sessionKey(slug))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSession
    return parsed.accessToken && parsed.refreshToken ? parsed : null
  } catch {
    return null
  }
}

export function writeSession(session: StoredSession, slug?: string): void {
  try {
    localStorage.setItem(sessionKey(slug), JSON.stringify(session))
  } catch {
    // Janela privada: a sessão vive só em memória nesta aba.
  }
}

export function clearSession(slug?: string): void {
  try {
    localStorage.removeItem(sessionKey(slug))
  } catch {
    // nada a limpar
  }
}

/**
 * Limpa também os caches do service worker DAQUELE slug. Sem isso, o treino
 * do aluno anterior continuaria legível no aparelho após o logout.
 */
export async function clearServiceWorkerCaches(slug: string): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    const keys = await caches.keys()
    await Promise.all(
      keys.filter((key) => key.startsWith(`steingym-${slug}-`)).map((key) => caches.delete(key)),
    )
  } catch {
    // Cache Storage indisponível; nada a limpar.
  }
}
